import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { signUp as signUpRequest, SignUpRequest } from '@/api/auth';
import { ROUTES } from '@/router/routes';
import { ROLES_LABELS } from '@/utils/constants';
import { toast_success } from '@/libs/toast-wrappers';
import { useCountriesOptions } from '@/hooks/api/use-countries-options';
import { emailSchema, phoneSchema, requiredString } from '@/utils/zod-schemas';

import { InputField } from '../form/input-field';
import { Button } from '../ui/button';
import { InputCheckbox } from '../form/input-checkbox';
import { SelectField } from '../form/select-field';

const schema = z.object({
  name: requiredString,
  email: emailSchema,
  phone: phoneSchema,
  country: requiredString,
  city: requiredString,
  provinceState: requiredString,
  clinic: z.string().trim().optional(),
  postal: z.string().trim().optional(),
  address: z.string().trim().optional(),
  requestedRole: requiredString,
});

type SignUpFormData = z.infer<typeof schema>;

export function SignUpForm() {
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SignUpFormData>({ resolver: zodResolver(schema) });
  const { COUNTRIES, isLoading: loadingCountries } = useCountriesOptions();

  const navigate = useNavigate();

  const onSubmit: SubmitHandler<SignUpFormData> = async (data) => {
    setIsLoading(true);
    try {
      // adding only required fields to the sign up data
      const signup_data: SignUpRequest = {
        name: data.name,
        user_email: data.email,
        phone: data.phone,
        city: data.city,
        provinceState: data.provinceState,
        country: data.country,
        requestedRole: data.requestedRole,
        created_at: new Date().toISOString(),
      };
      // adding some optional fields to the sign up data
      if (data.address) signup_data.address = data.address;
      if (data.postal) signup_data.postal = data.postal;
      if (data.clinic) signup_data.clinic = data.clinic;

      const response = await signUpRequest(signup_data);
      if (!response) {
        setIsLoading(false);
        return;
      }

      reset();
      toast_success(t('signup-success'));
      setTimeout(function () {
        void navigate(ROUTES.LOGIN);
      }, 2000);

      setIsLoading(false);
    } catch {
      setIsLoading(false);
    }
  };

  return (
    <>
      <h1 className="title-auth">{t('signup-title')}</h1>
      <form className="w-full my-12 flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
        <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-4">
          <InputField
            label={t('signup-full-name')}
            required
            autoComplete="name"
            classNameFieldSet="lg:col-span-2"
            type="text"
            autoFocus
            disabled={isLoading}
            errorMessage={errors.name?.message}
            {...register('name')}
          />
          <InputField
            label={t('signup-clinic-name')}
            autoComplete="organization"
            type="text"
            disabled={isLoading}
            {...register('clinic')}
          />
          <SelectField
            label={t('signup-role')}
            required
            options={ROLES_LABELS}
            enableEmptyOption
            disabled={isLoading}
            errorMessage={errors.requestedRole?.message}
            {...register('requestedRole')}
          />
          <InputField
            label={t('login-email')}
            type="email"
            required
            autoComplete="email"
            disabled={isLoading}
            errorMessage={errors.email?.message}
            {...register('email')}
          />
          <InputField
            label={t('signup-phone')}
            required
            autoComplete="tel"
            type="tel"
            disabled={isLoading}
            errorMessage={errors.phone?.message}
            {...register('phone')}
          />
          <InputField
            label={t('signup-address')}
            autoComplete="street-address"
            classNameFieldSet="lg:col-span-2"
            type="text"
            disabled={isLoading}
            {...register('address')}
          />
          <InputField
            label={t('signup-city')}
            autoComplete="address-level2"
            type="text"
            required
            disabled={isLoading}
            errorMessage={errors.city?.message}
            {...register('city')}
          />
          <InputField
            label={t('signup-state')}
            autoComplete="address-level1"
            type="text"
            required
            disabled={isLoading}
            errorMessage={errors.provinceState?.message}
            {...register('provinceState')}
          />
          <InputField
            label={t('signup-postal')}
            autoComplete="postal-code"
            type="text"
            disabled={isLoading}
            {...register('postal')}
          />
          <SelectField
            label={t('signup-country')}
            required
            options={COUNTRIES ?? []}
            enableEmptyOption
            autoComplete="country"
            disabled={loadingCountries || isLoading}
            errorMessage={errors.country?.message}
            {...register('country')}
          />
        </div>
        <InputCheckbox name="terms" required label={t('login-terms')} />

        <p className="indicates-required-field">{t('signup-required-legend')}</p>

        <div className="flex flex-wrap items-center gap-4">
          <Button loading={isLoading} type="submit" className="button">
            {t('signup-submit')}
          </Button>
          <div className="inline-flex gap-2 items-center">
            <span>{t('signup-have-account')}</span>
            <Button href={ROUTES.LOGIN} variant="link">
              {t('changepass-login')}
            </Button>
          </div>
        </div>
      </form>
    </>
  );
}
